/**
 * `Scene` -> SVG.
 *
 * Sem `innerHTML`: SVG exige `createElementNS`, e `innerHTML` num pai SVG produz
 * elementos no namespace HTML que simplesmente nao renderizam -- alem de ser porta de
 * injecao assim que o enunciado vier de um banco de dados.
 *
 * As formas carregam PAPEIS; as classes CSS levam a cor. Consequencia: tema
 * claro/escuro custa zero JavaScript.
 *
 * As regras de estilo abaixo sao o que separa "figura desenhada" de "figura plotada",
 * e todas moram aqui em vez de espalhadas por 85 templates.
 */

import type { Vec2 } from '../geom/vec.ts';
import { add, angleOf, dist, normalize, perp, scale, sub } from '../geom/vec.ts';
import { quantize } from '../core/num.ts';
import type { RichText } from '../schema/richtext.ts';
import { toPlainText } from '../schema/richtext.ts';
import type { LabelAnchor, LabelEmphasis, Rect, Scene, Shape, StrokeRole } from './scene.ts';
import { eixoDoEspelho } from './scene.ts';

const NS = 'http://www.w3.org/2000/svg';

export function h(
  tag: string,
  attrs: Record<string, string | number> = {},
  ...children: (Node | string)[]
): SVGElement {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  for (const c of children) {
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

export interface RenderOptions {
  /** Escala de fonte relativa; 1 = padrao. */
  readonly fontScale?: number;
  /** Mostrar a malha de fundo mesmo que a cena nao peca. */
  readonly showGrid?: boolean;
  readonly idPrefix?: string;
}

const q = (n: number): number => quantize(n, 4);

export function renderScene(scene: Scene, opts: RenderOptions = {}): SVGSVGElement {
  const vb = scene.viewBox;
  const svg = h('svg', {
    viewBox: `${q(vb.x)} ${q(vb.y)} ${q(vb.w)} ${q(vb.h)}`,
    class: 'scene',
    role: 'img',
    'aria-label': scene.a11y.title,
    preserveAspectRatio: 'xMidYMid meet',
  }) as SVGSVGElement;

  // Texto e pontos NAO escalam com o viewBox (ver `ajustarEscalaDeTela`). Ate a
  // primeira medida de layout vale uma estimativa; depois ela e corrigida.
  svg.style.setProperty('--scene-px', String(q(vb.w / LARGURA_ESTIMADA)));
  if (opts.fontScale !== undefined && opts.fontScale !== 1) {
    svg.style.setProperty('--font-scale', String(opts.fontScale));
  }

  const title = h('title');
  title.textContent = scene.a11y.title;
  const desc = h('desc');
  desc.textContent = scene.a11y.desc;
  svg.appendChild(title);
  svg.appendChild(desc);
  // Cada SVG carrega seus proprios `defs`: assim a figura continua correta quando
  // exportada isolada (impressao, PDF, copiar-colar), sem depender da pagina.
  svg.appendChild(svgDefs());

  // Inverte o eixo Y: no modelo, y cresce para cima (convencao matematica);
  // no SVG, para baixo. Fazer isso aqui e nao nos geradores evita que 85 templates
  // tenham que lembrar de negar coordenadas.
  const flip = h('g', { transform: `translate(0, ${q(eixoDoEspelho(vb))}) scale(1, -1)` });

  // Os rótulos soltos da cena (números dos eixos, títulos) são reservados antes de
  // tudo: o nome de um ponto desenhado depois desvia deles (ver `ancoraLivre`).
  const px = vb.w / LARGURA_ESTIMADA;
  const ocupado: Caixa[] = [];
  for (const s of achatar(scene.shapes)) {
    if (s.t === 'label' && s.rotate === undefined) {
      ocupado.push(caixaDoRotulo(s.at, s.anchor, toPlainText(s.text), scene.unit, px));
    }
  }
  const ctx: Ctx = { unit: scene.unit, viewBox: vb, px, ocupado };

  for (const s of scene.shapes) flip.appendChild(renderShape(s, ctx));
  svg.appendChild(flip);
  agendarAjusteDeEscala();
  return svg;
}

// ---------------------------------------------------------------------------
// Escala de tela: o que e TEXTO nao pode escalar com o viewBox
// ---------------------------------------------------------------------------

/**
 * Um rotulo dimensionado em unidades de MODELO cresce com a figura: num problema de
 * 400 unidades, `13 * unit` vira uma fonte de ~47 unidades, e o "A" fica do tamanho
 * do angulo que ele nomeia. Tamanho de texto e raio de ponto sao grandezas de TELA:
 * 13 px sao 13 px em qualquer enquadramento, com qualquer zoom.
 *
 * O elo entre os dois mundos e `--scene-px` = unidades de modelo por pixel de tela.
 * O CSS entao escreve `calc(var(--scene-px) * 13px)` e o resultado, depois da
 * transformacao do viewBox, mede exatamente 13 px na tela.
 */
const LARGURA_ESTIMADA = 720;

export function ajustarEscalaDeTela(svg: SVGSVGElement): void {
  const vb = lerViewBox(svg);
  if (!vb) return;
  const r = svg.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return;
  // `preserveAspectRatio="xMidYMid meet"`: vale a MENOR escala das duas.
  const k = Math.max(vb.w / r.width, vb.h / r.height);
  if (Number.isFinite(k) && k > 0) svg.style.setProperty('--scene-px', String(quantize(k, 5)));
}

/** O `viewBox` de um SVG já desenhado, lido do atributo. */
export function lerViewBox(svg: SVGSVGElement): Rect | null {
  const bruto = svg.getAttribute('viewBox');
  if (!bruto) return null;
  const n = bruto
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (n.length !== 4 || n.some((v) => !Number.isFinite(v))) return null;
  return { x: n[0] as number, y: n[1] as number, w: n[2] as number, h: n[3] as number };
}

let ajusteAgendado = false;

/**
 * Uma unica varredura por quadro, para todas as cenas da pagina. Um `ResizeObserver`
 * por figura seria mais preciso e vazaria: a galeria troca 60 cenas por vez.
 */
export function agendarAjusteDeEscala(): void {
  if (ajusteAgendado) return;
  if (typeof requestAnimationFrame === 'undefined' || typeof document === 'undefined') return;
  ajusteAgendado = true;
  requestAnimationFrame(() => {
    ajusteAgendado = false;
    for (const el of document.querySelectorAll('svg.scene')) {
      ajustarEscalaDeTela(el as SVGSVGElement);
    }
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('resize', agendarAjusteDeEscala, { passive: true });
}

/** A mesma Scene como string -- caminho de impressao/PDF, de graca. */
export function sceneToString(scene: Scene, opts: RenderOptions = {}): string {
  const el = renderScene(scene, opts);
  el.setAttribute('xmlns', NS);
  return new XMLSerializer().serializeToString(el);
}

interface Ctx {
  readonly unit: number;
  readonly viewBox: Rect;
  /** Unidades de modelo por pixel de tela, estimadas antes do leiaute. */
  readonly px: number;
  /** Caixas dos rótulos já postos; os nomes de ponto desviam delas. */
  readonly ocupado: Caixa[];
}

/** Caixa aproximada de um rótulo, pelo centro, em unidades de modelo. */
interface Caixa {
  readonly cx: number;
  readonly cy: number;
  readonly w: number;
  readonly h: number;
}

function* achatar(shapes: readonly Shape[]): Generator<Shape> {
  for (const s of shapes) {
    if (s.t === 'group') yield* achatar(s.children);
    else yield s;
  }
}

/**
 * Onde um rótulo cai, reproduzindo `renderLabel`: afastamento de 1 unidade mais 7 px na
 * direção da âncora, e o texto crescendo para o lado em que ela aponta. É uma
 * estimativa (a largura das letras varia), suficiente para ver que dois rótulos se
 * encostam.
 */
function caixaDoRotulo(
  at: Vec2,
  anchor: LabelAnchor,
  texto: string,
  unit: number,
  px: number,
): Caixa {
  const w = (7 * Math.max(1, [...texto].length) + 3) * px;
  const h = 13 * px;
  if (anchor === 'c') return { cx: at.x, cy: at.y, w, h };
  const dir = anchor === 'auto' ? ANCHOR_DIR.ne : ANCHOR_DIR[anchor];
  const base = add(at, scale(dir, 1.0 * unit + 7 * px));
  const sx = dir.x > 0.3 ? 1 : dir.x < -0.3 ? -1 : 0;
  const sy = dir.y > 0.3 ? 1 : dir.y < -0.3 ? -1 : 0;
  return { cx: base.x + (sx * w) / 2, cy: base.y + (sy * h) / 2, w, h };
}

const encostam = (a: Caixa, b: Caixa): boolean =>
  Math.abs(a.cx - b.cx) < (a.w + b.w) / 2 && Math.abs(a.cy - b.cy) < (a.h + b.h) / 2;

/**
 * O nome de um ponto vai a nordeste, como sempre — a menos que ali já haja um rótulo.
 * O caso que motivou: o ponto (−1, −1) num plano cartesiano, cujo nome caía em cima do
 * "0" da origem e se lia "C⁰".
 */
function ancoraLivre(p: Vec2, nome: string, ctx: Ctx): LabelAnchor {
  for (const a of ['ne', 'nw', 'se', 'sw'] as const) {
    const c = caixaDoRotulo(p, a, nome, ctx.unit, ctx.px);
    if (!ctx.ocupado.some((o) => encostam(o, c))) return a;
  }
  return 'ne';
}

function renderShape(s: Shape, ctx: Ctx): SVGElement {
  switch (s.t) {
    case 'group': {
      const g = h('g', s.opacity !== undefined ? { opacity: s.opacity } : {});
      if (s.key) g.setAttribute('data-key', s.key);
      for (const c of s.children) g.appendChild(renderShape(c, ctx));
      return g;
    }

    case 'seg':
      return withArrow(
        h('line', { x1: q(s.a.x), y1: q(s.a.y), x2: q(s.b.x), y2: q(s.b.y), class: cls(s.role) }),
        s.arrow ?? 'none',
      );

    case 'ray': {
      const [a, b] = clipRay(s.from, s.through, ctx.viewBox);
      return h('line', { x1: q(a.x), y1: q(a.y), x2: q(b.x), y2: q(b.y), class: cls(s.role) });
    }

    case 'line': {
      const [a, b] = clipLine(s.p, s.dir, ctx.viewBox);
      return h('line', { x1: q(a.x), y1: q(a.y), x2: q(b.x), y2: q(b.y), class: cls(s.role) });
    }

    case 'circle':
      return h('circle', {
        cx: q(s.c.x),
        cy: q(s.c.y),
        r: q(s.r),
        class: cls(s.role, s.fill),
      });

    case 'arc':
      return h('path', { d: arcPath(s.c, s.r, s.a0, s.a1), class: cls(s.role) });

    case 'poly':
      return h(s.closed ? 'polygon' : 'polyline', {
        points: s.pts.map((p) => `${q(p.x)},${q(p.y)}`).join(' '),
        class: cls(s.role, s.fill),
      });

    case 'rect':
      return h('rect', {
        x: q(s.at.x),
        y: q(s.at.y),
        width: q(s.at.w),
        height: q(s.at.h),
        class: cls(s.role, s.fill),
      });

    case 'point':
      return renderPoint(s.p, s.role, s.name, ctx);

    case 'angle':
      return renderAngle(s, ctx);

    case 'right-angle':
      return renderRightAngle(s.vertex, s.a, s.b, ctx);

    case 'ticks':
      return renderTicks(s.a, s.b, s.n, ctx);

    case 'dimension':
      return renderDimension(s.a, s.b, s.offset, s.label, ctx);

    case 'label':
      return renderLabel(s.at, s.text, s.anchor, ctx, s.role, s.rotate, s.emphasis);

    case 'grid':
      return renderGrid(s.extent, s.step, s.dots);

    case 'axes':
      return renderAxes(s.extent, s.step, ctx, s.xLabel, s.yLabel);
  }
}

function cls(role: StrokeRole, fill?: string): string {
  const base = `s-${role}`;
  return fill && fill !== 'none' ? `${base} f-${fill}` : base;
}

// ---------------------------------------------------------------------------
// Pontos: bolinha COM HALO
// ---------------------------------------------------------------------------

/**
 * Um ponto sobre uma reta, desenhado como um disco simples, funde-se com a reta e
 * deixa de se ler como ponto. O anel da cor do fundo resolve isso -- e e um daqueles
 * detalhes que separam figura profissional de figura amadora.
 *
 * O raio no atributo e proporcional e serve de RESERVA: o CSS o substitui por um raio
 * medido em pixels de tela (`r: calc(var(--scene-px) * ...)`). Onde a propriedade
 * geometrica de CSS nao existir, o desenho continua razoavel em vez de sumir.
 */
function renderPoint(p: Vec2, role: string, name: string | undefined, ctx: Ctx): SVGElement {
  const r = 1.15 * ctx.unit;
  const g = h('g', { class: `pt p-${role}` });
  g.appendChild(h('circle', { cx: q(p.x), cy: q(p.y), r: q(r * 1.75), class: 'pt-halo' }));
  g.appendChild(h('circle', { cx: q(p.x), cy: q(p.y), r: q(r), class: 'pt-dot' }));
  if (name) {
    const anchor = ancoraLivre(p, name, ctx);
    ctx.ocupado.push(caixaDoRotulo(p, anchor, name, ctx.unit, ctx.px));
    g.appendChild(renderLabel(p, [{ t: 'text', s: name }], anchor, ctx));
  }
  return g;
}

// ---------------------------------------------------------------------------
// Rotulos
// ---------------------------------------------------------------------------

const ANCHOR_DIR: Record<Exclude<LabelAnchor, 'auto' | 'c'>, Vec2> = {
  n: { x: 0, y: 1 },
  s: { x: 0, y: -1 },
  e: { x: 1, y: 0 },
  w: { x: -1, y: 0 },
  ne: { x: 0.707, y: 0.707 },
  nw: { x: -0.707, y: 0.707 },
  se: { x: 0.707, y: -0.707 },
  sw: { x: -0.707, y: -0.707 },
};

/**
 * O afastamento tem duas parcelas: uma proporcional, minima, que abre espaco em
 * figuras densas, e uma constante em PIXELS DE TELA, aplicada por CSS ja dentro do
 * contra-flip -- e ela que mantem o rotulo a mesma distancia do ponto sob qualquer
 * zoom. O tamanho da fonte vem inteiro do CSS (ver `--scene-px`).
 */
function renderLabel(
  at: Vec2,
  text: RichText,
  anchor: LabelAnchor,
  ctx: Ctx,
  role?: StrokeRole,
  rotate?: number,
  emphasis?: LabelEmphasis,
): SVGElement {
  const off = 1.0 * ctx.unit;
  const dir = anchor === 'auto' || anchor === 'c' ? { x: 0.707, y: 0.707 } : ANCHOR_DIR[anchor];
  const pos = anchor === 'c' ? at : add(at, scale(dir, off));

  // Contra-flip: o grupo pai esta espelhado em Y, e texto espelhado e ilegivel.
  // A rotacao entra DEPOIS do contra-flip, senao o titulo do eixo sai de cabeca
  // para baixo junto com a figura.
  const giro = rotate ? ` rotate(${-rotate})` : '';
  const g = h('g', { transform: `translate(${q(pos.x)}, ${q(pos.y)}) scale(1, -1)${giro}` });
  const classes = ['lbl'];
  if (role) classes.push(`s-${role}`);
  if (emphasis) classes.push(`lbl-${emphasis}`);
  // Âncora 'c' significa CENTRADO no ponto. Antes ela caía no vetor diagonal padrão
  // e o texto saía alinhado à esquerda, começando no ponto e crescendo para a direita
  // — um título de gráfico "centralizado" aparecia deslocado meia largura.
  const centrado = anchor === 'c';
  const t = h('text', {
    x: 0,
    y: 0,
    class: classes.join(' '),
    'text-anchor': centrado ? 'middle' : dir.x > 0.3 ? 'start' : dir.x < -0.3 ? 'end' : 'middle',
    'dominant-baseline': centrado
      ? 'middle'
      : dir.y > 0.3
        ? 'auto'
        : dir.y < -0.3
          ? 'hanging'
          : 'middle',
  });
  if (anchor !== 'c') {
    // Dentro do contra-flip o eixo y volta a apontar para baixo: dai o sinal trocado.
    const dx = q(dir.x * 7);
    const dy = q(-dir.y * 7);
    t.style.translate = `calc(var(--scene-px, 0.4) * ${dx}px) calc(var(--scene-px, 0.4) * ${dy}px)`;
  }
  t.textContent = toPlainText(text);
  g.appendChild(t);
  return g;
}

// ---------------------------------------------------------------------------
// Angulos
// ---------------------------------------------------------------------------

function renderAngle(s: Extract<Shape, { t: 'angle' }>, ctx: Ctx): SVGElement {
  const legA = dist(s.vertex, s.a);
  const legB = dist(s.vertex, s.b);
  // Raio proporcional ao menor lado, mas com piso e teto: um angulo num triangulo
  // muito fino nao pode virar um arco microscopico nem transbordar a figura.
  const base = Math.min(
    Math.max(0.18 * Math.min(legA, legB), 14 * ctx.unit * 0.9),
    34 * ctx.unit * 0.9,
  );

  const g = h('g', { class: 'ang' });
  const u = normalize(sub(s.a, s.vertex));
  const w = normalize(sub(s.b, s.vertex));
  const a0 = angleOf(u);
  const a1 = angleOf(w);

  // Arcos concentricos quando ha mais de uma marca (congruencia de angulos).
  for (let k = 0; k < s.ticks; k++) {
    const r = base * (1 + k * 0.22);
    g.appendChild(
      h('path', { d: arcPath(s.vertex, r, a0, a1), class: cls(s.role ?? 'auxiliary') }),
    );
  }

  if (s.label) {
    const mid = normalize(add(u, w));
    const at = add(s.vertex, scale(mid, base * 1.55));
    g.appendChild(renderLabel(at, s.label, 'c', ctx));
  }
  return g;
}

function renderRightAngle(vertex: Vec2, a: Vec2, b: Vec2, ctx: Ctx): SVGElement {
  const size = 9 * ctx.unit * 0.9;
  const u = scale(normalize(sub(a, vertex)), size);
  const w = scale(normalize(sub(b, vertex)), size);
  const p1 = add(vertex, u);
  const p2 = add(add(vertex, u), w);
  const p3 = add(vertex, w);
  return h('polyline', {
    points: [p1, p2, p3].map((p) => `${q(p.x)},${q(p.y)}`).join(' '),
    class: 's-auxiliary',
  });
}

function renderTicks(a: Vec2, b: Vec2, n: 1 | 2 | 3, ctx: Ctx): SVGElement {
  const g = h('g', { class: 'ticks' });
  const d = normalize(sub(b, a));
  const nrm = perp(d);
  const size = 5 * ctx.unit * 0.9;
  const gap = 3.5 * ctx.unit * 0.9;
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };

  for (let k = 0; k < n; k++) {
    const c = add(mid, scale(d, (k - (n - 1) / 2) * gap));
    const p1 = add(c, scale(nrm, size));
    const p2 = add(c, scale(nrm, -size));
    g.appendChild(
      h('line', { x1: q(p1.x), y1: q(p1.y), x2: q(p2.x), y2: q(p2.y), class: 's-auxiliary' }),
    );
  }
  return g;
}

// ---------------------------------------------------------------------------
// Cotas -- com linhas de extensao, como desenho tecnico
// ---------------------------------------------------------------------------

function renderDimension(
  a: Vec2,
  b: Vec2,
  offset: number,
  labelText: RichText,
  ctx: Ctx,
): SVGElement {
  const g = h('g', { class: 'dim' });
  const d = normalize(sub(b, a));
  const nrm = perp(d);
  const off = scale(nrm, offset);
  const a2 = add(a, off);
  const b2 = add(b, off);
  const ext = 4 * ctx.unit * 0.9;

  // linhas de extensao, com folguinha antes de encostar no objeto
  const eA = scale(nrm, Math.sign(offset) * ext);
  g.appendChild(
    h('line', {
      x1: q(add(a, eA).x),
      y1: q(add(a, eA).y),
      x2: q(add(a2, eA).x),
      y2: q(add(a2, eA).y),
      class: 's-auxiliary',
    }),
  );
  g.appendChild(
    h('line', {
      x1: q(add(b, eA).x),
      y1: q(add(b, eA).y),
      x2: q(add(b2, eA).x),
      y2: q(add(b2, eA).y),
      class: 's-auxiliary',
    }),
  );

  // linha de cota com vao central para o rotulo
  const mid = { x: (a2.x + b2.x) / 2, y: (a2.y + b2.y) / 2 };
  const vao = Math.min(dist(a2, b2) * 0.32, 17 * ctx.unit * 0.9);
  const m1 = add(mid, scale(d, -vao / 2));
  const m2 = add(mid, scale(d, vao / 2));

  g.appendChild(
    withArrow(
      h('line', { x1: q(a2.x), y1: q(a2.y), x2: q(m1.x), y2: q(m1.y), class: 's-auxiliary' }),
      'end',
    ),
  );
  g.appendChild(
    withArrow(
      h('line', { x1: q(b2.x), y1: q(b2.y), x2: q(m2.x), y2: q(m2.y), class: 's-auxiliary' }),
      'end',
    ),
  );
  g.appendChild(renderLabel(mid, labelText, 'c', ctx));
  return g;
}

// ---------------------------------------------------------------------------
// Malha e eixos
// ---------------------------------------------------------------------------

/**
 * A malha anda por ÍNDICE, não por soma acumulada.
 *
 * `x += step` acumula erro de ponto flutuante: com passo 46/3, depois de algumas
 * linhas o valor passa um fio do limite e a última linha some — justamente a que
 * fecha a borda de uma malha recortada nas próprias linhas. Com `x = i × step` cada
 * linha é calculada do zero, e os limites têm uma folga explícita.
 */
function renderGrid(extent: Rect, step: number, dots: boolean): SVGElement {
  const g = h('g', { class: 'grid' });
  const folga = 1e-6;
  const i0 = Math.ceil(extent.x / step - folga);
  const i1 = Math.floor((extent.x + extent.w) / step + folga);
  const j0 = Math.ceil(extent.y / step - folga);
  const j1 = Math.floor((extent.y + extent.h) / step + folga);

  if (dots) {
    for (let i = i0; i <= i1; i++) {
      for (let j = j0; j <= j1; j++) {
        g.appendChild(h('circle', { cx: q(i * step), cy: q(j * step), r: 0.9, class: 'grid-dot' }));
      }
    }
    return g;
  }

  for (let i = i0; i <= i1; i++) {
    const x = i * step;
    g.appendChild(
      h('line', {
        x1: q(x),
        y1: q(extent.y),
        x2: q(x),
        y2: q(extent.y + extent.h),
        class: 'grid-line',
      }),
    );
  }
  for (let j = j0; j <= j1; j++) {
    const y = j * step;
    g.appendChild(
      h('line', {
        x1: q(extent.x),
        y1: q(y),
        x2: q(extent.x + extent.w),
        y2: q(y),
        class: 'grid-line',
      }),
    );
  }
  return g;
}

function renderAxes(
  extent: Rect,
  step: number,
  ctx: Ctx,
  xLabel?: string,
  yLabel?: string,
): SVGElement {
  const g = h('g', { class: 'axes' });
  const x1 = extent.x;
  const x2 = extent.x + extent.w;
  const y1 = extent.y;
  const y2 = extent.y + extent.h;

  g.appendChild(
    withArrow(h('line', { x1: q(x1), y1: 0, x2: q(x2), y2: 0, class: 's-auxiliary' }), 'end'),
  );
  g.appendChild(
    withArrow(h('line', { x1: 0, y1: q(y1), x2: 0, y2: q(y2), class: 's-auxiliary' }), 'end'),
  );

  const tick = 3.5 * ctx.unit * 0.9;
  for (let x = Math.ceil(x1 / step) * step; x <= x2; x += step) {
    if (Math.abs(x) < 1e-9) continue;
    g.appendChild(h('line', { x1: q(x), y1: -tick, x2: q(x), y2: tick, class: 's-auxiliary' }));
  }
  for (let y = Math.ceil(y1 / step) * step; y <= y2; y += step) {
    if (Math.abs(y) < 1e-9) continue;
    g.appendChild(h('line', { x1: -tick, y1: q(y), x2: tick, y2: q(y), class: 's-auxiliary' }));
  }

  if (xLabel) g.appendChild(renderLabel({ x: x2, y: 0 }, [{ t: 'text', s: xLabel }], 'se', ctx));
  if (yLabel) g.appendChild(renderLabel({ x: 0, y: y2 }, [{ t: 'text', s: yLabel }], 'nw', ctx));
  return g;
}

// ---------------------------------------------------------------------------
// Utilitarios geometricos de desenho
// ---------------------------------------------------------------------------

function withArrow(el: SVGElement, arrow: 'none' | 'end' | 'both'): SVGElement {
  if (arrow === 'end' || arrow === 'both') el.setAttribute('marker-end', 'url(#arrow)');
  if (arrow === 'both') el.setAttribute('marker-start', 'url(#arrow-start)');
  return el;
}

function arcPath(c: Vec2, r: number, a0: number, a1: number): string {
  let delta = a1 - a0;
  while (delta <= -Math.PI) delta += 2 * Math.PI;
  while (delta > Math.PI) delta -= 2 * Math.PI;

  const p0 = { x: c.x + r * Math.cos(a0), y: c.y + r * Math.sin(a0) };
  const p1 = { x: c.x + r * Math.cos(a0 + delta), y: c.y + r * Math.sin(a0 + delta) };
  const largeArc = Math.abs(delta) > Math.PI ? 1 : 0;
  const sweep = delta > 0 ? 1 : 0;

  return `M ${q(p0.x)} ${q(p0.y)} A ${q(r)} ${q(r)} 0 ${largeArc} ${sweep} ${q(p1.x)} ${q(p1.y)}`;
}

/**
 * RECORTE AO QUADRO (Liang-Barsky).
 *
 * Semirreta e reta nao tem fim, e o SVG precisa de dois pontos. A versao antiga
 * estendia a direcao por 1,2 vez a diagonal do quadro a partir da origem da semirreta
 * -- o que nao recorta nada: se a origem estivesse no meio do quadro, a linha saia
 * quase meia diagonal para fora dele.
 *
 * Na tela isso passava: `svg.scene` tem `overflow: visible` de proposito, para nao
 * cortar o halo de um ponto nem a perna de um rotulo na borda, e sobra espaco em volta
 * da figura. **No papel, nao.** Ali as questoes se encostam, e o eixo de simetria de
 * uma construcao descia 380 unidades alem do quadro, riscando o enunciado de baixo.
 *
 * Agora o recorte e de verdade: devolve o pedaco da reta que cai DENTRO do quadro.
 * Quando nao ha pedaco nenhum dentro, devolve um segmento de comprimento zero, que nao
 * desenha nada.
 */
function recortar(p: Vec2, d: Vec2, box: Rect, tMin: number, tMax: number): [Vec2, Vec2] {
  let t0 = tMin;
  let t1 = tMax;
  // Uma dupla por borda do quadro: quanto a direcao avanca contra ela, e quanto falta.
  const bordas: readonly (readonly [number, number])[] = [
    [-d.x, p.x - box.x],
    [d.x, box.x + box.w - p.x],
    [-d.y, p.y - box.y],
    [d.y, box.y + box.h - p.y],
  ];
  for (const [avanco, folga] of bordas) {
    if (Math.abs(avanco) < 1e-12) {
      // Paralela a esta borda: ou esta dentro da faixa, ou a reta inteira esta fora.
      if (folga < 0) return [p, p];
      continue;
    }
    const t = folga / avanco;
    if (avanco < 0) t0 = Math.max(t0, t);
    else t1 = Math.min(t1, t);
  }
  if (t0 > t1) return [p, p];
  return [add(p, scale(d, t0)), add(p, scale(d, t1))];
}

function clipRay(from: Vec2, through: Vec2, box: Rect): [Vec2, Vec2] {
  const d = normalize(sub(through, from));
  // A semirreta comeca na origem dela e nao anda para tras: `tMin = 0`.
  return recortar(from, d, box, 0, Math.hypot(box.w, box.h) * 2);
}

function clipLine(p: Vec2, dir: Vec2, box: Rect): [Vec2, Vec2] {
  const d = normalize(dir);
  const alcance = Math.hypot(box.w, box.h) * 2;
  return recortar(p, d, box, -alcance, alcance);
}

/** Definicoes reutilizaveis (marcadores de seta). Inserir uma vez por pagina. */
export function svgDefs(): SVGElement {
  const defs = h('defs');
  const marker = h('marker', {
    id: 'arrow',
    viewBox: '0 0 10 10',
    refX: 9,
    refY: 5,
    markerWidth: 5,
    markerHeight: 5,
    orient: 'auto-start-reverse',
    markerUnits: 'strokeWidth',
  });
  marker.appendChild(h('path', { d: 'M 0 1 L 10 5 L 0 9 z', class: 'arrowhead' }));
  defs.appendChild(marker);
  return defs;
}
